import { DEFAULT_CURRENCY } from '../constants'
import { DEFAULT_REGION, Region, type SupportedRegion } from '../enums'
import {
  bcp47LanguageToLanguageMap,
  countriesBySupportedRegionMap,
  hotlineLanguageToLanguageMap,
  languageToAwsTranslateLanguageMap,
  languageToNativeLanguageMap,
  regionToRegionNameMap,
  supportedRegionToRegionMap,
} from './convertersMap'
import { isCountryInEuropeanUnion } from './countryAreas'
import { BCP47Language, Country, Currency, type HotlineLanguage, Language } from './enums'
import { DEFAULT_LANGUAGE, languageEnumToBasename } from './languages'

export const replaceVariablesInString = (str: string, replacers: Record<string, string | number>) =>
  str.replace(/\{([0-9a-zA-Z_]+)\}/g, (_, key) => `${replacers[key] ?? ''}`)

// `new Intl.DisplayNames(...)` is expensive to construct and these converters run
// per-row/per-render for country and language fields, so cache one instance per
// (locales, type) combination instead of rebuilding it on every call.
const localesCacheKey = (locales: Intl.LocalesArgument) =>
  Array.isArray(locales) ? locales.join(',') : String(locales)

const displayNamesCache = new Map<string, Intl.DisplayNames>()

const getDisplayNames = (locales: Intl.LocalesArgument, type: Intl.DisplayNamesType) => {
  const key = `${type}:${localesCacheKey(locales)}`
  const cached = displayNamesCache.get(key)
  if (cached) {
    return cached
  }
  const displayNames = new Intl.DisplayNames(locales, { type })
  displayNamesCache.set(key, displayNames)
  return displayNames
}

// `Intl.DisplayNames.of(...)` is itself non-trivial and these converters run
// per-row/per-render over a small, bounded set of country/language codes, so
// memoize the resolved name per (type, locales, code) on top of the instance cache.
const displayNameResultCache = new Map<string, string | undefined>()

const getDisplayName = (
  locales: Intl.LocalesArgument,
  type: Intl.DisplayNamesType,
  code: string
) => {
  const key = `${type}:${localesCacheKey(locales)}:${code}`
  if (displayNameResultCache.has(key)) {
    return displayNameResultCache.get(key)
  }
  const result = getDisplayNames(locales, type).of(code)
  displayNameResultCache.set(key, result)
  return result
}

export const convertCountryToCountryName = (country: Country, locale = 'en') =>
  getDisplayName([locale, 'en'], 'region', country.toUpperCase()) ?? country

export const convertLanguageToLanguageName = (language: Language, locale = 'en') =>
  getDisplayName([locale, 'en'], 'language', languageEnumToBasename(language)) ?? language

export const translateLanguageName = (language: Language, targetLanguage: BCP47Language) =>
  getDisplayName(targetLanguage, 'language', languageEnumToBasename(language))

const getObjectValueByKey = <T, K extends keyof T>(key: K, obj: T) => obj[key]

// Reverse lookups (value -> keys) used to run an O(n) scan over the source map on
// every call. These source maps are module-level constants, so build the reverse
// index once per map (keyed by the map object) and reuse it. Multiplicity is
// preserved so callers relying on the result length (e.g. ambiguous-language
// checks) keep working.
const reverseLookupCache = new WeakMap<object, Map<unknown, string[]>>()

const buildReverseIndex = (obj: Record<string, string | number | null>) => {
  const index = new Map<unknown, string[]>()
  for (const key of Object.keys(obj)) {
    const existing = index.get(obj[key])
    if (existing) {
      existing.push(key)
    } else {
      index.set(obj[key], [key])
    }
  }
  return index
}

const getObjectKeysByValueCached = <T extends Record<string, string | number | null>>(
  value: T[keyof T],
  obj: T
) => {
  const cached = reverseLookupCache.get(obj)
  const reverseIndex = cached ?? buildReverseIndex(obj)
  if (!cached) {
    reverseLookupCache.set(obj, reverseIndex)
  }
  return (reverseIndex.get(value) ?? []) as (keyof T)[]
}

export const convertBCP47LanguageToLanguage = (bcp47Language: BCP47Language): Language =>
  getObjectValueByKey(bcp47Language, bcp47LanguageToLanguageMap) ?? DEFAULT_LANGUAGE

export const convertLanguageToBCP47Language = (language: Language): BCP47Language =>
  getObjectKeysByValueCached(language, bcp47LanguageToLanguageMap)[0] ?? BCP47Language.en_US

export const convertRegionToRegionName = (region: Region | SupportedRegion): string =>
  getObjectValueByKey(region, regionToRegionNameMap)

export const convertLanguageToAwsTranslateLanguage = (language: Language): string | null =>
  getObjectValueByKey(language, languageToAwsTranslateLanguageMap)

export const convertAwsTranslateLanguageToLanguage = (language: string): Language | null =>
  getObjectKeysByValueCached(language, languageToAwsTranslateLanguageMap)[0] ?? null

export const convertSupportedRegionToRegion = (region: SupportedRegion): Region =>
  getObjectValueByKey(region, supportedRegionToRegionMap)

export const convertRegionToSupportedRegion = (region: Region): SupportedRegion => {
  const supportedRegions = getObjectKeysByValueCached(region, supportedRegionToRegionMap)

  if (!supportedRegions[0]) {
    throw Error(`Region ${region} is not supported`)
  }

  return supportedRegions[0]
}

export const convertCountryToSupportedRegion = (country: Country): SupportedRegion => {
  const regionEntry = Object.entries(countriesBySupportedRegionMap).find(([, countries]) =>
    countries.includes(country)
  )

  return regionEntry
    ? (regionEntry[0] as SupportedRegion)
    : convertRegionToSupportedRegion(DEFAULT_REGION)
}

export const convertHotlineLanguageToLanguage = (hotlineLanguage: HotlineLanguage): Language =>
  getObjectValueByKey(hotlineLanguage, hotlineLanguageToLanguageMap)

export const convertLanguageToHotlineLanguage = (language: Language): HotlineLanguage => {
  const hotlineLanguages = getObjectKeysByValueCached(language, hotlineLanguageToLanguageMap)

  if (!hotlineLanguages[0] || hotlineLanguages.length > 1) {
    throw Error(`Language ${language} is not supported`)
  }

  return hotlineLanguages[0]
}

export const convertLanguageToNativeLanguage = (language: Language): string =>
  getObjectValueByKey(language, languageToNativeLanguageMap)

export const convertLanguageForChargebee = (language: Language) => {
  switch (language) {
    case Language.Cs:
    case Language.Sk:
      return 'cs'
    default:
      return 'en-US'
  }
}

// currency is defined in Chargebee and this array copies that
export const availableCurrencies = [Currency.CZK, Currency.EUR, Currency.USD, Currency.GBP] as const
export type AvailableCurrency = (typeof availableCurrencies)[number]

export const getAvailableCurrencyFromCountry = (country: Country): AvailableCurrency => {
  if (country === Country.Cz) {
    return Currency.CZK
  }

  if (country === Country.Gb) {
    return Currency.GBP
  }

  if (isCountryInEuropeanUnion(country)) {
    return Currency.EUR
  }

  return DEFAULT_CURRENCY
}

export const parseRegion = (value: string | null): Region | null =>
  Region[value as keyof typeof Region] ?? null

export const convertLanguageToFormattedLanguage = (lang: string): string => {
  const parts = lang.split('_')
  if (parts.length < 2) {
    return lang.toLowerCase()
  }

  return `${parts[0]?.toLowerCase()}-${parts[1]?.toUpperCase()}`
}
